import React, { useEffect, useState, useRef } from 'react';
import api from '../../api';
import ParticipantOiTable from '../../components/ParticipantOiTable';

const POLL_MS = 10000; // 10 seconds

const STATUS_ICONS = {
  BREAKOUT_SUSTAINED: '🟢',
  BREAKDOWN_SUSTAINED: '🔻',
  APPROACHING_LEVEL: '⚠️',
  APPROACHING_RESISTANCE: '⚠️',
  APPROACHING_SUPPORT: '⚠️',
  PATTERN_EMA_PULLBACK: '📈',
  PATTERN_RTS_FLIP: '📈',
  PATTERN_STR_FLIP: '📉',
  MARKET_CLOSED: '🕒',
  IDLE: 'ℹ️'
};

const STATUS_CLASS_MAP = {
  BREAKOUT_SUSTAINED: 'banner-success',
  PATTERN_EMA_PULLBACK: 'banner-success',
  PATTERN_RTS_FLIP: 'banner-success',
  BREAKDOWN_SUSTAINED: 'banner-danger',
  PATTERN_STR_FLIP: 'banner-danger',
  APPROACHING_LEVEL: 'banner-warning',
  APPROACHING_RESISTANCE: 'banner-warning',
  APPROACHING_SUPPORT: 'banner-warning',
  MARKET_CLOSED: 'banner-info',
  IDLE: 'banner-info'
};

const PatternDisplay = ({ patterns }) => {
  if (!Array.isArray(patterns) || patterns.length === 0) {
    return (
      <div className="pattern-container rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h4 className="text-base font-semibold text-gray-800 mb-2">Chart Patterns</h4>
        <p className="text-sm text-gray-600">No significant patterns detected.</p>
      </div>
    );
  }

  return (
    <div className="pattern-container rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h4 className="text-base font-semibold text-gray-800 mb-4">Chart Patterns Detected</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {patterns.map((pattern, index) => {
          const keyLevels = pattern.key_levels || {};
          const signalClass =
            pattern.signal === 'Bullish'
              ? 'border-green-200 bg-green-50'
              : 'border-red-200 bg-red-50';
          return (
            <div
              key={`${pattern.pattern_name}-${pattern.timeframe}-${index}`}
              className={`rounded-lg border ${signalClass} p-4`}
            >
              <div className="flex items-center justify-between">
                <h5 className="text-lg font-semibold text-gray-800">
                  {pattern.pattern_name} ({pattern.timeframe})
                </h5>
                <span
                  className={`text-xs uppercase font-semibold px-2 py-1 rounded ${
                    pattern.signal === 'Bullish'
                      ? 'bg-green-200 text-green-800'
                      : 'bg-red-200 text-red-800'
                  }`}
                >
                  {pattern.signal}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-700">
                <strong>Status:</strong> {pattern.status}
              </p>
              <div className="mt-3 text-sm text-gray-700 space-y-1">
                {keyLevels.resistance && <p>Resistance: {keyLevels.resistance}</p>}
                {keyLevels.support && <p>Support: {keyLevels.support}</p>}
                {keyLevels.neckline && <p>Neckline: {keyLevels.neckline}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MarketStatusBanner = ({ status }) => {
  if (!status) return null;
  const bannerClass = STATUS_CLASS_MAP[status.type] || STATUS_CLASS_MAP.IDLE;
  const icon = STATUS_ICONS[status.type] || STATUS_ICONS.IDLE;
  return (
    <div className={`market-status-banner ${bannerClass} rounded-lg p-4 border`}>
      <div className="flex items-center">
        <span className="mr-2 text-lg">{icon}</span>
        <p className="text-sm sm:text-base font-medium">{status.message || 'Monitoring market conditions...'}</p>
      </div>
    </div>
  );
};

const NiftySignalPage = () => {
  const [signal, setSignal] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  // Check if market is open (IST: Mon-Fri, 09:15 - 15:30)
  const isMarketOpen = () => {
    const now = new Date();
    // Convert UTC to IST (UTC+5:30)
    // Get UTC time in milliseconds
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
    // Add IST offset (5 hours 30 minutes = 5.5 * 60 * 60 * 1000 ms)
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istTime = new Date(utcTime + istOffset);
    
    const weekday = istTime.getUTCDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const hour = istTime.getUTCHours();
    const minute = istTime.getUTCMinutes();
    
    // Market is closed on weekends
    if (weekday === 0 || weekday === 6) {
      return false;
    }
    
    // Market hours: 09:15 to 15:30 IST
    const marketStart = 9 * 60 + 15; // 09:15 in minutes
    const marketEnd = 15 * 60 + 30; // 15:30 in minutes
    const currentMinutes = hour * 60 + minute;
    
    return currentMinutes >= marketStart && currentMinutes <= marketEnd;
  };

  const fetchSignal = async () => {
    try {
      const resp = await api.get('/api/v1/tools/nifty-signal-analyzer');
      console.log('📊 Signal response:', resp.data);
      setSignal(resp.data);
      setError('');
      
      // Log ATM data availability
      if (resp.data?.atm_option_chain) {
        console.log('✅ ATM option chain data received:', resp.data.atm_option_chain);
      } else {
        console.log('⚠️ No ATM option chain data in response');
      }
      
      // Log Participant OI data availability
      if (resp.data?.status === 'post_market_prediction') {
        const postMarket = resp.data.post_market_prediction || resp.data;
        if (postMarket?.participant_oi_data) {
          console.log(`✅ Participant OI data received: ${postMarket.participant_oi_data.length} records`);
          console.log('📋 Sample participant OI data:', postMarket.participant_oi_data.slice(0, 2));
        } else {
          console.log('⚠️ No participant OI data in post-market prediction response');
        }
      } else if (resp.data?.participant_oi_data) {
        console.log(`✅ Participant OI data received (direct): ${resp.data.participant_oi_data.length} records`);
      } else {
        console.log('⚠️ No participant OI data in response');
      }
    } catch (e) {
      console.error('❌ Error fetching signal:', e);
      
      // Handle timeout errors with user-friendly message
      if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        const timeoutMsg = 'The market analysis is taking longer than expected. Please wait a moment and the data will appear automatically.';
        setError(timeoutMsg);
      } else {
        const msg = e?.response?.data?.detail || e.message || 'An unexpected error occurred while fetching data. Please try refreshing the page.';
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // initial fetch
    fetchSignal();
    
    // Polling: Only poll frequently during market hours, less frequently after hours
    const startPolling = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
      
      if (isMarketOpen()) {
        // Market is open: poll every 10 seconds
        pollRef.current = setInterval(fetchSignal, POLL_MS);
        console.log('✅ Market is open - polling every', POLL_MS / 1000, 'seconds');
      } else {
        // Market is closed: poll every 5 minutes (300000ms) to check for post-market updates
        pollRef.current = setInterval(fetchSignal, 300000);
        console.log('⏰ Market is closed - polling every 5 minutes');
      }
    };
    
    startPolling();
    
    // Re-check market status every minute to switch polling frequency
    const marketCheckInterval = setInterval(() => {
      startPolling();
    }, 60000); // Check every minute
    
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (marketCheckInterval) clearInterval(marketCheckInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLoading = () => (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-40 bg-gray-200 rounded" />
      </div>
    </div>
  );

  const renderNoSignal = () => {
    const atmData = signal?.atm_option_chain;
    const marketStatus = signal?.market_status;
    
    return (
      <div className="waiting-card max-w-4xl mx-auto px-4 py-8">
        {/* Dynamic Market Status Banner */}
        <div className="mb-6">
          <MarketStatusBanner status={marketStatus} />
        </div>

        {/* Only show Chart Patterns when market is open */}
        {marketStatus?.type !== 'MARKET_CLOSED' && (
          <div className="mb-6">
            <PatternDisplay patterns={signal?.patterns} />
          </div>
        )}

        {atmData ? (
          <div className="atm-option-chain bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="mb-4">
              <h4 className="text-lg font-semibold text-gray-800 mb-2">ATM Option Chain Data</h4>
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                <span><strong>NIFTY Spot:</strong> ₹{atmData.spot_price}</span>
                <span><strong>ATM Strike:</strong> {atmData.atm_strike}</span>
                <span><strong>Expiry:</strong> {new Date(atmData.expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                <span className="text-xs text-gray-500">Last updated: {new Date(atmData.timestamp).toLocaleTimeString()}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Call Option (CE) */}
              <div className="rounded border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-semibold text-blue-800">CALL (CE)</h5>
                  <span className="text-sm text-blue-600">{atmData.ce.tradingsymbol}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">LTP:</span>
                    <span className="font-semibold">₹{atmData.ce.ltp}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delta:</span>
                    <span className="font-semibold">{atmData.ce.delta.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Theta:</span>
                    <span className="font-semibold text-red-600">{atmData.ce.theta.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vega:</span>
                    <span className="font-semibold">{atmData.ce.vega.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">IV:</span>
                    <span className="font-semibold">{atmData.ce.iv.toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gamma:</span>
                    <span className="font-semibold">{atmData.ce.gamma.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-blue-200">
                    <span className="text-gray-600">OI:</span>
                    <span className="font-semibold">{atmData.ce.oi.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Volume:</span>
                    <span className="font-semibold">{atmData.ce.volume.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Put Option (PE) */}
              <div className="rounded border border-red-200 bg-red-50 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-semibold text-red-800">PUT (PE)</h5>
                  <span className="text-sm text-red-600">{atmData.pe.tradingsymbol}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">LTP:</span>
                    <span className="font-semibold">₹{atmData.pe.ltp}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delta:</span>
                    <span className="font-semibold">{atmData.pe.delta.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Theta:</span>
                    <span className="font-semibold text-red-600">{atmData.pe.theta.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vega:</span>
                    <span className="font-semibold">{atmData.pe.vega.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">IV:</span>
                    <span className="font-semibold">{atmData.pe.iv.toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gamma:</span>
                    <span className="font-semibold">{atmData.pe.gamma.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-red-200">
                    <span className="text-gray-600">OI:</span>
                    <span className="font-semibold">{atmData.pe.oi.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Volume:</span>
                    <span className="font-semibold">{atmData.pe.volume.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 text-xs text-gray-500 text-center">
              Data updates automatically as NIFTY50 price changes. Refreshing every {POLL_MS / 1000} seconds...
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <p className="text-sm text-gray-600 text-center">
              ATM option chain data is being fetched... Please wait a moment.
            </p>
          </div>
        )}
        
        {/* Participant OI Data Table - Only visible when market is closed */}
        {!isMarketOpen() && (
          <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <ParticipantOiTable data={signal?.participant_oi_data} isLoading={isLoading} />
          </div>
        )}
      </div>
    );
  };

  const getNextTradingDay = () => {
    const now = new Date();
    // Convert to IST
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
    const istOffsetMs = 5.5 * 60 * 60 * 1000;
    const istNow = new Date(utcMs + istOffsetMs);

    const next = new Date(istNow);
    next.setDate(istNow.getDate() + 1);

    let weekday = next.getUTCDay();
    // If Saturday (6) or Sunday (0), jump forward to Monday
    if (weekday === 6) {
      next.setDate(next.getDate() + 2);
    } else if (weekday === 0) {
      next.setDate(next.getDate() + 1);
    }

    return next.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const renderSignalCard = (s) => {
    // Handle both old format (s is signal) and new format (s is signal.signal)
    const signalData = s.signal_type ? s : (signal?.signal || s);
    const bullish = signalData.signal_type === 'Bullish';
    const tagClass = bullish
      ? 'inline-flex items-center px-2 py-1 rounded text-sm font-semibold bg-green-100 text-green-800'
      : 'inline-flex items-center px-2 py-1 rounded text-sm font-semibold bg-red-100 text-red-800';
    const action = bullish ? 'BUY' : 'SELL';
    const headerIcon = bullish ? '🟢 NEW BULLISH SIGNAL' : '🔴 NEW BEARISH SIGNAL';
    const marketStatus = signal?.market_status;

    return (
      <div className="signal-card-container max-w-3xl mx-auto px-4 py-8">
        {/* Dynamic Market Status Banner */}
        <div className="mb-6">
          <MarketStatusBanner status={marketStatus} />
        </div>
        {/* Only show Chart Patterns when market is open */}
        {marketStatus?.type !== 'MARKET_CLOSED' && (
          <div className="mb-6">
            <PatternDisplay patterns={signal?.patterns} />
          </div>
        )}
        <div className="signal-card rounded-lg border border-gray-200 shadow-sm bg-white">
          <div className="card-header flex items-center justify-between p-4 border-b border-gray-100">
            <span className={tagClass}>{headerIcon}</span>
            <span className="text-sm text-gray-500">{new Date(signalData.timestamp).toLocaleTimeString()}</span>
          </div>
          <div className="card-body p-6">
            <h4 className="text-sm font-semibold text-gray-600">ACTION: {action}</h4>
            <h3 className="mt-1 text-2xl font-bold tracking-tight">{signalData.instrument}</h3>
            <div className="price-grid mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="price-point rounded border border-gray-100 p-4">
                <label className="text-xs uppercase tracking-wider text-gray-500">ENTRY PRICE</label>
                <p className="text-lg font-semibold mt-1">~ ₹{Number(signalData.entry_price).toFixed(2)}</p>
              </div>
              <div className="price-point target rounded border border-green-100 bg-green-50 p-4">
                <label className="text-xs uppercase tracking-wider text-gray-600">TARGET</label>
                <p className="text-lg font-semibold mt-1 text-green-800">₹{Number(signalData.target).toFixed(2)}</p>
              </div>
              <div className="price-point stoploss rounded border border-red-100 bg-red-50 p-4">
                <label className="text-xs uppercase tracking-wider text-gray-600">STOP-LOSS</label>
                <p className="text-lg font-semibold mt-1 text-red-800">₹{Number(signalData.stop_loss).toFixed(2)}</p>
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-700">Risk:Reward Ratio: {signalData.risk_reward_ratio}</p>
          </div>
          <div className="card-footer p-6 border-t border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700">▼ AI Analysis Breakdown:</h4>
            <p className="mt-2 text-sm text-gray-800">✅ <strong>Trend Confirmation (30-Min):</strong> {signalData.reasoning?.bias}</p>
            <p className="mt-1 text-sm text-gray-800">✅ <strong>Entry Trigger (5-Min):</strong> {signalData.reasoning?.trigger}</p>
            {Array.isArray(signalData.warnings) && signalData.warnings.length > 0 && (
              <div className="warnings-section mt-3 rounded border border-orange-200 bg-orange-50 p-4">
                <h4 className="text-sm font-semibold text-orange-800">⚠️ Warnings:</h4>
                {signalData.warnings.map((w, idx) => (
                  <p key={idx} className="text-sm text-orange-900 mt-1">• {w}</p>
                ))}
              </div>
            )}

            {signalData.key_levels && (
              <div className="outlook-section mt-6">
                <h4 className="text-sm font-semibold text-gray-700">Key Technical Levels</h4>
                <div className="levels-container grid grid-cols-1 sm:grid-cols-2 gap-6 mt-3">
                  
                  {/* Daily Levels Column */}
                  <div className="levels-column rounded border border-gray-100 p-4">
                    <h5 className="text-sm font-semibold text-gray-800">For Next Day</h5>
                    <div className="pivot-point mt-2">
                      <strong>Pivot Point: {signalData.key_levels?.daily?.pivot}</strong>
                    </div>
                    <div className="resistance-levels mt-3">
                      <div className="text-xs uppercase tracking-wider text-gray-600 mb-1">Resistance</div>
                      {signalData.key_levels?.daily?.resistance?.map((r) => (
                        <p key={r.level} className="text-sm"><span>{r.level}:</span> <strong>{r.value}</strong></p>
                      ))}
                    </div>
                    <div className="support-levels mt-2">
                      <div className="text-xs uppercase tracking-wider text-gray-600 mb-1">Support</div>
                      {signalData.key_levels?.daily?.support?.map((lv) => (
                        <p key={lv.level} className="text-sm"><span>{lv.level}:</span> <strong>{lv.value}</strong></p>
                      ))}
                    </div>
                  </div>

                  {/* Weekly Levels Column (Conditional) */}
                  {signalData.key_levels?.weekly && (
                    <div className="levels-column rounded border border-gray-100 p-4">
                      <h5 className="text-sm font-semibold text-gray-800">For Next Week</h5>
                      <div className="pivot-point mt-2">
                        <strong>Pivot Point: {signalData.key_levels?.weekly?.pivot}</strong>
                      </div>
                      <div className="resistance-levels mt-3">
                        <div className="text-xs uppercase tracking-wider text-gray-600 mb-1">Resistance</div>
                        {signalData.key_levels?.weekly?.resistance?.map((r) => (
                          <p key={r.level} className="text-sm"><span>{r.level}:</span> <strong>{r.value}</strong></p>
                        ))}
                      </div>
                      <div className="support-levels mt-2">
                        <div className="text-xs uppercase tracking-wider text-gray-600 mb-1">Support</div>
                        {signalData.key_levels?.weekly?.support?.map((lv) => (
                          <p key={lv.level} className="text-sm"><span>{lv.level}:</span> <strong>{lv.value}</strong></p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Participant OI Data Table - Only visible when market is closed */}
          {!isMarketOpen() && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-6">
              <ParticipantOiTable data={signalData.participant_oi_data || signal?.participant_oi_data} isLoading={isLoading} />
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) return renderLoading();

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="rounded border border-red-200 bg-red-50 p-6 text-red-800">
          {error}
        </div>
      </div>
    );
  }

  // Part 3: Handle data_error status
  if (signal && signal.status === 'data_error') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="error-card rounded-lg border border-red-300 bg-red-50 shadow-md p-6">
          <div className="flex items-center mb-4">
            <span className="text-2xl mr-3">⚠️</span>
            <h3 className="text-xl font-semibold text-red-800">Data Fetching Error</h3>
          </div>
          <p className="text-base text-red-700 mb-3">
            There was a problem retrieving complete market data from the broker. The analysis cannot be performed at this time.
          </p>
          {signal.message && (
            <div className="mt-4 p-4 bg-red-100 rounded border border-red-200">
              <p className="text-sm font-semibold text-red-800 mb-1">Details:</p>
              <p className="text-sm text-red-700">{signal.message}</p>
            </div>
          )}
          <div className="mt-4 text-sm text-red-600">
            <p>This may be due to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Insufficient historical data available from the broker</li>
              <li>Network connectivity issues</li>
              <li>Broker API limitations for older dates</li>
            </ul>
            <p className="mt-3">Please try refreshing the page in a few moments.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!signal || signal.status === 'no_active_signal') return renderNoSignal();

  if (signal && signal.status === 'overnight_outlook') {
    const outlook = signal.outlook || {};
    const factors = Array.isArray(outlook.factors) ? outlook.factors : [];

    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="outlook-card rounded-lg border border-indigo-300 bg-indigo-50 shadow-md p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
            <div className="flex items-center gap-2 text-indigo-800 font-semibold text-sm sm:text-base">
              <span>⏳ OVERNIGHT GAP OUTLOOK</span>
            </div>
            <div className="text-xs sm:text-sm text-indigo-700 mt-2 sm:mt-0">
              For Trade on: <span className="font-semibold">{getNextTradingDay()}</span>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              SIGNAL:{' '}
              <span className="text-indigo-700">
                {outlook.signal || 'Neutral / Uncertain Outlook'}
              </span>
            </h3>
            <p className="mt-1 text-sm text-gray-700">
              CONFIDENCE:{' '}
              <span className="font-semibold">{outlook.confidence || 'Low'}</span>
            </p>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">
              AI ANALYSIS &amp; SUPPORTING FACTORS (as of {outlook.timestamp || 'N/A'}):
            </h4>
            {factors.length === 0 ? (
              <p className="text-sm text-gray-600">
                No external market factors were available at this time.
              </p>
            ) : (
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                {factors.map((factor, index) => (
                  <li key={index}>
                    {/(positive|strong|up|higher|\+)/i.test(factor) ? '✅' : '❌'} {factor}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-4 text-xs text-yellow-900 bg-yellow-50 border border-yellow-300 rounded-md p-3">
            ⚠️ <span className="font-semibold">IMPORTANT:</span> Holding trades overnight carries significant risk due
            to unforeseen global events and gap openings. This is a probabilistic AI outlook, not a guaranteed result
            or investment advice.
          </div>
        </div>
      </div>
    );
  }

  if (signal.status === 'active_signal') return renderSignalCard(signal.signal || signal);
  if (signal.status === 'post_market_prediction') {
    const s = signal.post_market_prediction || signal;
    const marketStatus = signal?.market_status;
    
    // Debug logging
    console.log('📊 Post-market data:', {
      hasParticipantData: !!s.participant_oi_data,
      participantDataLength: s.participant_oi_data?.length,
      participantData: s.participant_oi_data
    });
    
    // Build ordered array for Daily levels: R3, R2, R1, Pivot, S1, S2, S3
    const dailyLevelsData = s.key_levels?.daily;
    const dailyOrderedLevels = dailyLevelsData ? [
      ...[...(dailyLevelsData.resistance || [])].reverse(), // Reverse resistance: R3, R2, R1
      { level: 'Pivot', value: dailyLevelsData.pivot },
      ...(dailyLevelsData.support || []) // S1, S2, S3
    ] : [];

    // Build ordered array for Weekly levels: R3, R2, R1, Pivot, S1, S2, S3
    const weeklyLevelsData = s.key_levels?.weekly;
    const weeklyOrderedLevels = weeklyLevelsData ? [
      ...[...(weeklyLevelsData.resistance || [])].reverse(), // Reverse resistance: R3, R2, R1
      { level: 'Pivot', value: weeklyLevelsData.pivot },
      ...(weeklyLevelsData.support || []) // S1, S2, S3
    ] : [];

    return (
      <div className="post-market-card max-w-3xl mx-auto px-4 py-8">
        {/* Dynamic Market Status Banner */}
        {marketStatus && (
          <div className="mb-6">
            <MarketStatusBanner status={marketStatus} />
          </div>
        )}
        {/* Only show Chart Patterns when market is open */}
        {marketStatus?.type !== 'MARKET_CLOSED' && (
          <div className="mb-6">
            <PatternDisplay patterns={signal?.patterns} />
          </div>
        )}
        <div className="rounded-lg border border-gray-200 shadow-sm bg-white">
          <div className="card-header flex items-center justify-between p-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold">Post-Market Analysis: Next Day Range Prediction</h3>
            <span className="text-sm text-gray-500">For: {s.prediction_for_date}</span>
          </div>
          <div className="card-body p-6">
            <div className="prediction-inputs text-sm text-gray-700">
              <p className="mb-1">Based on Previous Close:</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <span>NIFTY 50: <strong>{s.based_on?.nifty_close}</strong></span>
                <span>INDIA VIX: <strong>{s.based_on?.vix_close}</strong></span>
                {s.based_on?.gift_nifty && (
                  <span>GIFT NIFTY: <strong>{s.based_on.gift_nifty}</strong></span>
                )}
              </div>
            </div>
            <div className="prediction-outputs grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              <div className="range-point high rounded border border-green-100 bg-green-50 p-4">
                <label className="text-xs uppercase tracking-wider text-gray-600">Predicted High</label>
                <p className="text-xl font-semibold mt-1 text-green-800">~ {Math.round(s.predicted_range?.high)}</p>
              </div>
              <div className="range-point low rounded border border-red-100 bg-red-50 p-4">
                <label className="text-xs uppercase tracking-wider text-gray-600">Predicted Low</label>
                <p className="text-xl font-semibold mt-1 text-red-800">~ {Math.round(s.predicted_range?.low)}</p>
              </div>
            </div>
            {s.directional_bias && (
              <div className="outlook-section mt-6">
                <h4 className="text-sm font-semibold text-gray-700">Global Sentiment &amp; Directional Bias</h4>
                <div className={`bias-indicator mt-2 px-3 py-2 rounded border ${
                  s.directional_bias.bias?.toLowerCase().includes('positive') ? 'bg-green-50 border-green-200 text-green-800' :
                  s.directional_bias.bias?.toLowerCase().includes('negative') ? 'bg-red-50 border-red-200 text-red-800' :
                  'bg-gray-50 border-gray-200 text-gray-700'
                }`}>
                  <h5 className="text-sm font-semibold">
                    {s.directional_bias.bias} (Score: {s.directional_bias.score})
                  </h5>
                </div>
                <p className="mt-2 text-sm text-gray-800">{s.directional_bias.description}</p>
                {((Array.isArray(s.directional_bias.factors) && s.directional_bias.factors.length > 0) ||
                  (Array.isArray(s.directional_bias.contributing) && s.directional_bias.contributing.length > 0)) && (
                  <div className="factors-list mt-3">
                    <strong className="text-sm text-gray-700">Key Contributing Factors:</strong>
                    <ul className="list-disc list-inside text-sm text-gray-800 mt-1">
                      {(s.directional_bias.factors || s.directional_bias.contributing || []).map((factor, idx) => (
                        <li key={idx}>{factor}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {s.key_levels && (
              <div className="key-levels-section mt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Key Technical Levels</h4>
                <div className="levels-container grid grid-cols-1 lg:grid-cols-1 gap-4 lg:gap-6">
                  
                  {/* Daily Levels Column */}
                  {dailyOrderedLevels.length > 0 && (
                    <div className="levels-card">
                      <h5 className="text-sm font-semibold text-gray-800">For Next Day</h5>
                      <table className="pivot-table">
                        <thead>
                          <tr>
                            {dailyOrderedLevels.map((item) => (
                              <th
                                key={item.level}
                                className={
                                  item.level.startsWith('R') ? 'resistance-header' :
                                  item.level.startsWith('S') ? 'support-header' : ''
                                }
                              >
                                {item.level}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {dailyOrderedLevels.map((item) => (
                              <td
                                key={item.level}
                                className={item.level === 'Pivot' ? 'bg-gray-50' : ''}
                              >
                                {item.value ? Number(item.value).toFixed(2) : '-'}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Weekly Levels Column (Conditional) */}
                  {s.key_levels?.weekly && weeklyOrderedLevels.length > 0 && (
                    <div className="levels-card">
                      <h5 className="text-sm font-semibold text-gray-800">For Next Week</h5>
                      <table className="pivot-table">
                        <thead>
                          <tr>
                            {weeklyOrderedLevels.map((item) => (
                              <th
                                key={item.level}
                                className={
                                  item.level.startsWith('R') ? 'resistance-header' :
                                  item.level.startsWith('S') ? 'support-header' : ''
                                }
                              >
                                {item.level}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {weeklyOrderedLevels.map((item) => (
                              <td
                                key={item.level}
                                className={item.level === 'Pivot' ? 'bg-gray-50' : ''}
                              >
                                {item.value ? Number(item.value).toFixed(2) : '-'}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Participant OI Data Table - Only visible when market is closed */}
          {!isMarketOpen() && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-6">
              {(() => {
                const oiData = s.participant_oi_data;
                console.log('🔍 Participant OI Data Check:', {
                  exists: oiData !== undefined && oiData !== null,
                  isArray: Array.isArray(oiData),
                  length: oiData?.length || 0,
                  data: oiData
                });
                return <ParticipantOiTable data={oiData} isLoading={isLoading} />;
              })()}
            </div>
          )}
          
          <div className="card-footer p-6 border-t border-gray-100">
            <details className="text-sm text-gray-800">
              <summary className="cursor-pointer">How is this calculated?</summary>
              <p className="mt-2">{s.explanation}</p>
            </details>
            <p className="disclaimer text-xs text-gray-500 mt-3">{s.disclaimer}</p>
          </div>
        </div>
      </div>
    );
  }

  return renderNoSignal();
};

export default NiftySignalPage;


